import React from 'react';
import { useCart } from 'react-use-cart';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react';

import Thumbnail from '~/components/modules/cards/products/thumbnail';
import Dropdown from '~/components/modules/cards/products/dropdown';
import Button from '~/components/modules/buttons/default';
import UpdateItemsForm from '~/components/modules/forms/update-items';
import ButtonGroup from '~/components/modules/buttons/button-group';
import ToolkitsLabel from '~/components/modules/cards/products/toolkits-label';
import ItemTitle from '~/components/modules/cards/products/item-title';
import ItemMeta from '~/components/modules/cards/products/item-meta';
import RemoveItemButton from '~/components/modules/buttons/remove-item';
import OrderButton from '~/components/modules/buttons/order';

import trackEvent from '~/hooks/useEventTracker';

import ArrowCollapseDown from '@iconify/icons-mdi/arrow-collapse-down';
import LinkVariant from '@iconify/icons-mdi/link-variant';
import GoToCart from '@iconify/icons-mdi/cart-arrow-up';

const siteConfig = require(`~/data/site/site.${process.env.GATSBY_SITE}.json`);

const ProductCard = ({
	product,
	mode = 'orderMode',
	noIncludedToolkit,
	embedMode,
	className
}) => {
	const { getItem } = useCart();

	const item =
		mode === 'orderMode'
			? product.node
				? product.node.data
				: product.data
			: product.item;
	if (!item.id) {
		item.id = product.id ?? product.recordId ?? product.node?.recordId;
	}

	const LINK_FILE_TYPES = ['YouTube', 'Vimeo', 'Link'];

	return (
		<div
			className={`
				bg-white border border-gray-300 shadow-md
				${className ?? ''}
			`}
		>
			{item.Order_Review && (
				<p className="bg-red-800 text-white py-1 px-2 text-xs">
					<span role="img" aria-label="warning">
						⚠️
					</span>{' '}
					Requesting this item will require your order to be reviewed and we may
					reach out to get more information.
				</p>
			)}

			<div className="relative md:grid md:grid-cols-[1fr,_3fr] lg:grid-cols-[1fr,_3.5fr,_2fr] gap-4 lg:gap-6 md:p-4 lg:p-6">
				{mode !== 'orderMode' && <RemoveItemButton cartItem={product} />}

				<Thumbnail
					className="hidden md:block"
					item={item}
					preventPreview={embedMode}
					mode={mode}
				/>

				<div className="text-xs flex flex-col p-4 md:p-0">
					{mode === 'orderMode' && item.Toolkits && !noIncludedToolkit && (
						<ToolkitsLabel item={item} embedMode={embedMode} />
					)}

					<ItemTitle item={item} />

					{item.Description && (
						<ReactMarkdown
							className={`
							${mode === 'orderMode' ? 'prose prose-sm' : 'text-xs'}
						`}
						>
							{item.Description}
						</ReactMarkdown>
					)}
				</div>
				<div className="flex flex-col justify-start md:col-span-2 lg:col-span-1 p-4 md:px-0 md:pb-0 lg:p-0 border-t lg:border-0 border-gray-200">
					{mode === 'orderMode' ? (
						((item.File_Type &&
							item.Availability?.some(
								availability => availability.data.Payload
							)) ||
							(item.Availability &&
								item.Availability.some(
									availability => availability.data.Orderable
								))) && (
							<div className="border-t border-b md:border-0 border-gray-200 -mx-4 md:mx-0 p-4 md:p-0">
								{item.File_Type &&
									item.Availability?.some(
										availability => availability.data.Payload
									) && (
										<Dropdown
											className={
												item.Availability &&
												item.Availability.some(
													availability => availability.data.Orderable
												)
													? 'mb-1.5'
													: ''
											}
											panelClassName="p-4"
											button={
												<Button
													as="button"
													variant="primary"
													size="sm"
													className="w-full flex items-center justify-between !text-2xs"
												>
													{LINK_FILE_TYPES.some(
														type => type === item.File_Type[0].data.Name
													) ? (
														<>
															<span className="flex-1 text-left">Link</span>
															<Icon
																icon={LinkVariant}
																height="1rem"
																width="1rem"
																className="w-3 h-3 ml-2"
															/>
														</>
													) : (
														<>
															<span className="flex-1 text-left">Download</span>
															<Icon
																icon={ArrowCollapseDown}
																height="1rem"
																width="1rem"
																className="w-3 h-3 ml-2"
															/>
														</>
													)}
												</Button>
											}
										>
											<ButtonGroup
												title={`${item.File_Type[0].data.Name}:`}
												titleClassName="text-2xs font-bold"
											>
												{item.Availability &&
													item.Availability.filter(
														availableItem => availableItem.data.Payload
													).map(availableItem => (
														<Button
															as="a"
															key={availableItem.recordId}
															variant="outline"
															size="sm"
															className="w-full flex items-center justify-between !text-2xs capitalize"
															to={availableItem.data.Payload}
															onClick={() => {
																trackEvent(
																	'Engagement',
																	'Download',
																	availableItem.data.SKU
																);
															}}
														>
															<span>
																{availableItem.data.Availability}
																{availableItem.data.Filesize && (
																	<span className="font-normal text-black group-hover:text-white text-opacity-50 pl-1">
																		({availableItem.data.Filesize})
																	</span>
																)}
															</span>

															<Icon
																icon={
																	LINK_FILE_TYPES.some(
																		type => type === item.File_Type[0].data.Name
																	)
																		? LinkVariant
																		: ArrowCollapseDown
																}
																height="1rem"
																width="1rem"
																className="w-3 h-3 ml-2"
															/>
														</Button>
													))}
											</ButtonGroup>
										</Dropdown>
									)}

								{item.Availability &&
									item.Availability.some(
										availability => availability.data.Orderable
									) && (
										<>
											{item.Status === 'Out of Stock' && (
												<p className="bg-red-800 text-white p-2 font-semibold text-xs">
													Out of Stock
												</p>
											)}

											{item.Status === 'Available' && (
												<>
													<OrderButton item={item} />

													{embedMode && (
														<Button
															as="a"
															variant="tertiary"
															size="sm"
															className="w-full mt-2 flex items-center justify-between !text-2xs capitalize"
															to={`${siteConfig.url}cart`}
															onClick={() => {
																trackEvent(
																	'Engagement',
																	'Go to Cart',
																	`${item.Name} ${item.Item_Type[0].data.Name}`
																);
															}}
														>
															Go to Cart
															<Icon
																icon={GoToCart}
																height="1rem"
																width="1rem"
																className="w-3 h-3 ml-2"
															/>
														</Button>
													)}
												</>
											)}
										</>
									)}
							</div>
						)
					) : (
						<UpdateItemsForm product={product} />
					)}

					{mode === 'orderMode' && item.Availability && (
						<>
							{item.Availability.map(availability => (
								<div key={availability.recordId}>
									{availability.data.Order_Review_Quantity &&
										availability.data.Order_Review_Quantity >= 0 &&
										getItem(availability.recordId)?.quantity >=
											availability.data.Order_Review_Quantity && (
											<p className="bg-red-800 text-white py-1 px-2 text-xs mt-2">
												<span role="img" aria-label="warning">
													⚠️
												</span>{' '}
												Requesting{' '}
												<span className="font-bold">
													{availability.data.Order_Review_Quantity}
												</span>{' '}
												or more of the{' '}
												<span className="font-bold">
													{availability.data.Availability}
												</span>{' '}
												version of this item will require your order to be
												reviewed.
											</p>
										)}
								</div>
							))}
						</>
					)}

					{mode === 'orderMode' && item.Last_Modified && (
						<p className="text-2xs italic text-gray-500 mt-3 text-center md:text-left">
							Last updated:{' '}
							<time dateTime={item.Last_Modified}>
								{dayjs(item.Last_Modified).format('MM/D/YY')}
							</time>
						</p>
					)}

					<div className="grid md:block grid-cols-[1fr,_1.5fr] gap-6 order-first md:order-last mb-4 md:mb-0 md:mt-3">
						<div className="md:hidden">
							<Thumbnail item={item} preventPreview={embedMode} />
						</div>

						{mode === 'orderMode' && (
							<ItemMeta item={item} className="text-2xs" />
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProductCard;
